import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { Button, EmptyState } from "@playbookai/ui";
import { clearTabSession } from "../store/tab-session.js";
import { useAuthStore } from "./auth-store.js";
import { authProductMark, SessionUnavailableScreen } from "./auth-screens.js";
import { logout } from "./session-api.js";

type Phase = "idle" | "signing-out" | "failed";

/**
 * Mounted on `/auth/sign-out`, OUTSIDE the page guard (PB-5461, CONTRACT §4).
 * The Keycloak login page links here ("Not you? Sign out").
 *
 * It NEVER signs out just by loading, so no other website can sign a person
 * out by sending them a link:
 *   - with a live app session it asks "Sign out of <email>?" and waits for the
 *     click, which runs the normal sign-out;
 *   - with no app session there is nothing here to confirm, so it asks the BFF
 *     for Keycloak's sign-out address and goes there: Keycloak's own "Do you
 *     want to sign out?" page asks for the click instead.
 */
export function SignOutPage(): ReactElement {
  const status = useAuthStore((s) => s.status);
  const claims = useAuthStore((s) => s.claims);
  const [phase, setPhase] = useState<Phase>("idle");
  const startedRef = useRef(false);

  const signOut = useCallback((): void => {
    // Once any sign-out has begun, the session it ends must not read as a
    // reason to start another one from the effect below.
    startedRef.current = true;
    setPhase("signing-out");
    logout().then(
      ({ url }) => {
        window.location.assign(url);
      },
      () => {
        setPhase("failed");
      },
    );
  }, []);

  useEffect(() => {
    if (status !== "unauthenticated" || startedRef.current) return;
    startedRef.current = true;
    signOut();
  }, [status, signOut]);

  const confirm = (): void => {
    // The same clean-up as the profile menu's sign-out: this person's tabs
    // must not be restored for whoever signs in next in this browser tab.
    if (claims?.tenantId && claims.userId) {
      clearTabSession({ tenantId: claims.tenantId, userId: claims.userId });
    }
    signOut();
  };

  if (status === "unavailable" && phase === "idle") {
    return <SessionUnavailableScreen />;
  }

  if (phase === "failed") {
    return (
      <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
        {authProductMark}
        {/* The page-level failure screen: EmptyState lg (OK-33). */}
        <EmptyState
          variant="error"
          size="lg"
          title={<span role="alert">Could not sign out.</span>}
          description="The sign-in service is not responding."
          action={
            <Button variant="secondary" onClick={signOut}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (status === "authenticated" && phase === "idle") {
    const who = claims?.email ?? claims?.name ?? null;
    return (
      <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
        {authProductMark}
        <h1 className="text-lg font-medium text-pb-text">
          {who ? `Sign out of ${who}?` : "Sign out?"}
        </h1>
        <Button variant="primary" onClick={confirm}>
          Sign out
        </Button>
      </div>
    );
  }

  /* Kept as plain text, like the guard's waiting line (OK-38). */
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {authProductMark}
      <p>{status === "unknown" ? "Checking your session…" : "Signing out…"}</p>
    </div>
  );
}
